import React, { useState, useEffect, useCallback, useMemo } from "react";
import styles from "./ProgressTracker.module.scss";
import { withTranslation } from "react-i18next";
import UpperCaseText from "../../../../utils/UpperCaseText";
import { ORDER_STATUS, RESERVATION_STATUS } from "../../../../common/Constants";
import LowerCaseText from "../../../../utils/LowerCaseText";

const CANCELLED_STATUSES = [
  "DEALER_CANCELLED",
  "CONTRACT_CANCELLED",
  "RESERVATION_CANCELLED",
  "ORDER_CANCELLED",
  "DEALER_REJECTED",
];

function ProgressTracker({ reservation, t }) {
  const [steps, setSteps] = useState([]);
  const [language, setLanguage] = useState(localStorage.getItem("lang"));

  const stepTemplates = useMemo(() => ({
    allActiveReservationSteps: [
      {
        number: 1,
        name: t("ProgressTracker.reservationPlacedEU"),
        style: styles.active,
      },
      {
        number: 2,
        name: t("CustomerReservations.deliveryScheduled"),
        style: styles.active,
      },
    ],
    allActiveOrderSteps: [
      {
        number: 1,
        name: t("ProgressTracker.orderPlacedEU"),
        style: styles.active,
      },
      {
        number: 2,
        name: t("ProgressTracker.inProductionEU"),
        style: styles.active,
      },
      {
        number: 3,
        name: t("ProgressTracker.shippingEU"),
        style: styles.active,
      },
      {
        number: 4,
        name: t("ProgressTracker.deliveredEU"),
        style: styles.active,
      },
    ],
    allDisabledReservationSteps: [
      {
        number: 1,
        name: t("ProgressTracker.reservationPlacedEU"),
        style: "",
      },
      {
        number: 2,
        name: t("CustomerReservations.deliveryScheduled"),
        style: "",
      },
    ],
    allDisabledOrderSteps: [
      {
        number: 1,
        name: t("ProgressTracker.orderPlacedEU"),
        style: "",
      },
      {
        number: 2,
        name: t("ProgressTracker.inProductionEU"),
        style: "",
      },
      {
        number: 3,
        name: t("ProgressTracker.shippingEU"),
        style: "",
      },
      {
        number: 4,
        name: t("ProgressTracker.deliveredEU"),
        style: "",
      },
    ],
  }), [t]);

  const addReservationSteps = useCallback((index, cancelled) => {
    const allSteps = [];

    for (let i = 0; i < 2; i++) {
      if (i > index && cancelled) {
        break;
      } else if (i > index) {
        allSteps.push(stepTemplates.allDisabledReservationSteps[i]);
      } else {
        allSteps.push(stepTemplates.allActiveReservationSteps[i]);
      }
    }

    return allSteps;
  }, [stepTemplates]);

  const addOrderSteps = useCallback((index, cancelled) => {
    const allSteps = [];

    for (let i = 0; i < 4; i++) {
      if (i > index && cancelled) {
        break;
      } else if (i > index) {
        allSteps.push(stepTemplates.allDisabledOrderSteps[i]);
      } else {
        allSteps.push(stepTemplates.allActiveOrderSteps[i]);
      }
    }

    return allSteps;
  }, [stepTemplates]);

  const getIndex = useCallback((status, type) => {
    if (type) {
      for (const [key, value] of Object.entries(ORDER_STATUS)) {
        for (let i = 0; i < value.length; i++) {
          if (value[i] === status) {
            return parseInt(key);
          }
        }
      }
    } else {
      for (const [key, value] of Object.entries(RESERVATION_STATUS)) {
        for (let i = 0; i < value.length; i++) {
          if (value[i] === status) {
            return parseInt(key);
          }
        }
      }
    }
  }, []);

  const getReservationSteps = useCallback((status, wasReservation) => {
    let index = wasReservation ? 1 : 0;
    index = index === undefined ? -1 : index;
    return addReservationSteps(index, false);
  }, [addReservationSteps]);

  const getCancelledReservationSteps = useCallback((
    status,
    cancelStatus,
    wasReservation,
    isRejected
  ) => {
    let index = wasReservation ? 1 : 0;

    if (status) {
      index = getIndex(status, false);
    }

    const steps = addReservationSteps(index, true);

    steps.push({
      number: index + 2,
      name: isRejected
        ? t("ProgressTracker.rejected")
        : t("ProgressTracker.cancelledEU"),
      style: styles.active,
    });

    return steps;
  }, [addReservationSteps, getIndex, t]);

  const getOrderSteps = useCallback((status) => {
    let index = 0;
    if (status) {
      index = getIndex(status, true);
    }
    return addOrderSteps(index, false);
  }, [addOrderSteps, getIndex]);

  const getCancelledOrderSteps = useCallback((status, cancelStatus, isRejected) => {
    let index = 0;

    if (status) {
      index = getIndex(status, true);
    }

    const steps = addOrderSteps(index, true);

    steps.push({
      number: index + 2,
      name: isRejected
        ? t("ProgressTracker.rejected")
        : t("ProgressTracker.cancelledEU"),
      style: styles.active,
    });

    return steps;
  }, [addOrderSteps, getIndex, t]);

  const getSteps = useCallback(() => {
    const isReservation = UpperCaseText(reservation.orderType) === "RESERVATION";
    const wasReservation = reservation.conversionDate || reservation.status === "CONVERTED_TO_OFFLINE";
    const isCancelled = CANCELLED_STATUSES.includes(reservation.status);
    const isRejected = !!reservation.dealerRejectedDate;

    if (isCancelled || isRejected) {
      if (isReservation || wasReservation) {
        return getCancelledReservationSteps(
          LowerCaseText(reservation.vehicleStatusCode),
          reservation.status,
          wasReservation,
          isRejected
        );
      } else {
        return getCancelledOrderSteps(
          LowerCaseText(reservation.vehicleStatusCode),
          reservation.status,
          isRejected
        );
      }
    } else {
      if (isReservation || wasReservation) {
        return getReservationSteps(
          LowerCaseText(reservation.vehicleStatusCode),
          wasReservation
        );
      } else {
        return getOrderSteps(
          LowerCaseText(reservation.vehicleStatusCode)
        );
      }
    }
  }, [
    reservation,
    getCancelledReservationSteps,
    getCancelledOrderSteps,
    getReservationSteps,
    getOrderSteps
  ]);

  const getInfo = useCallback(() => {
    const vista = reservation.vistaOrderNumber;
    const orbit = reservation.orbitId;
    const created = reservation.orbitIdCreatedDate;

    return (
      <div className={styles.subheaderDiv}>
        {vista && (
          <div className={styles.subheaderText}>
            {t("ProgressTracker.addedToVista")}
          </div>
        )}
        <div className={styles.subheaderTextBold}>
          {vista && (
            <span>
              {t("ProgressTracker.vista")}
              {vista}
            </span>
          )}
          {vista && orbit && <span>&nbsp;|&nbsp;</span>}
          {orbit && (
            <span>
              {t("ProgressTracker.orbit")}
              {orbit}
            </span>
          )}
          {(vista || orbit) && created && (
            <span>
              &nbsp;({t("ProgressTracker.entered")}{" "}
              {created.replace(/-/gi, "/")})
            </span>
          )}
        </div>
      </div>
    );
  }, [reservation, t]);

  const getCancelledInfo = useCallback((status) => {
    const cancelledBy =
      status === "DEALER_CANCELLED" || status === "ORDER_CANCELLED"
        ? t("ProgressTracker.dealer")
        : t("ProgressTracker.consumer");

    return (
      <div className={styles.subheaderDiv}>
        <div className={styles.subheaderTextBold}>
          {t("ProgressTracker.cancelledBy")} {cancelledBy}
        </div>
        <div className={styles.subheaderText}>
          {t("ProgressTracker.cancelledSubheader")}
        </div>
      </div>
    );
  }, [t]);

  useEffect(() => {
    setSteps(getSteps());
  }, [getSteps]);

  useEffect(() => {
    const currentLanguage = localStorage.getItem("lang");
    if (language !== currentLanguage) {
      setLanguage(currentLanguage);
      setSteps(getSteps());
    }
  }, [language, getSteps]);

  const orderCancelled = CANCELLED_STATUSES.includes(reservation.status);

  return (
    <div className={styles.progressTrackerWrapper}>
      <div className={styles.progressTrackerHeader}>
        {t("ProgressTracker.header")}
      </div>
      <div
        className={
          orderCancelled ? styles.progressBarCancelled : styles.progressBar
        }
      >
        {steps.map((step, index) => {
          return (
            <div className={styles.navTab} key={index}>
              {index > 0 ? (
                <div className={styles.connectingLine}>
                  <span className={step.style}>&nbsp;</span>
                </div>
              ) : (
                ""
              )}
              <div className={styles.navStepButton}>
                <div className={`${styles.progressTabNumber} ${step.style}`}>
                  <span
                    data-testid={`${step.style}${step.number}`}
                    className={`${styles.stepNumber} ${step.style}`}
                  >
                    {step.number}
                  </span>
                </div>
                <div className={`${styles.progressTab} ${step.style}`}>
                  {step.name}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {orderCancelled
        ? getCancelledInfo(reservation.status)
        : getInfo()}
    </div>
  );
}

export default withTranslation("emp")(ProgressTracker);
